import React, { Children, cloneElement, createContext, isValidElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import { Divider } from '../Divider';
export type TabsVariant = 'scrollable' | 'fullWidth';
export type TabsMode = 'tabs' | 'navigation';
interface TabsContextValue {
  activeTab: string;
  onTabChange: (value: string) => void;
  variant: TabsVariant;
  mode: TabsMode;
  registerTab: (value: string, element: HTMLButtonElement) => void;
  unregisterTab: (value: string) => void;
}
const TabsContext = createContext<TabsContextValue | null>(null);
export const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};
interface TabsProps {
  /** Callback when tab changes */
  onChange?: (value: string) => void;
  /** Tabs variant - scrollable or fullWidth */
  variant?: TabsVariant;
  /** Tabs mode - tabs (default) or navigation */
  mode?: TabsMode;
  /** Active tab value (for navigation mode) */
  activeTab?: string;
  /** Whether to show divider on the bottom of Tabs */
  withDivider?: boolean;
  /** Callback when tab is clicked in navigation mode */
  onTabClick?: (value: string) => void;
  /** Tab components as children */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Data ID for testing */
  dataId?: string;
  /** ARIA label for accessibility */
  'aria-label'?: string;
}
const TabsContainer = /*#__PURE__*/styled('div')({
  name: "TabsContainer",
  class: "tjj3ttp",
  propsAsIs: false
});
const TabsList = /*#__PURE__*/styled('div')({
  name: "TabsList",
  class: "tnnkb3",
  propsAsIs: false
});
const DividerWrapper = /*#__PURE__*/styled('div')({
  name: "DividerWrapper",
  class: "d1c18354",
  propsAsIs: false
});
const ActiveIndicator = /*#__PURE__*/styled('div')({
  name: "ActiveIndicator",
  class: "a1qygce6",
  propsAsIs: false
});
export const Tabs = ({
  onChange,
  variant = 'scrollable',
  mode = 'tabs',
  activeTab,
  onTabClick,
  children,
  className,
  withDivider = true,
  dataId,
  'aria-label': ariaLabel
}: TabsProps) => {
  const [tabElements, setTabElements] = useState<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  }>({
    left: 0,
    width: 0
  });
  const tabsListRef = useRef<HTMLDivElement>(null);
  const registerTab = useCallback((value: string, element: HTMLButtonElement) => {
    setTabElements(prev => new Map(prev).set(value, element));
  }, []);
  const unregisterTab = useCallback((value: string) => {
    setTabElements(prev => {
      const newMap = new Map(prev);
      newMap.delete(value);
      return newMap;
    });
  }, []);
  const updateIndicatorPosition = useCallback(() => {
    const activeElement = activeTab ? tabElements.get(activeTab) : null;
    const tabsList = tabsListRef.current;
    if (activeElement && tabsList) {
      const tabsListRect = tabsList.getBoundingClientRect();
      const activeRect = activeElement.getBoundingClientRect();
      const left = activeRect.left - tabsListRect.left + tabsList.scrollLeft;
      const {
        width
      } = activeRect;
      setIndicatorStyle(prev => {
        // Only update if position actually changed
        if (prev.left !== left || prev.width !== width) {
          return {
            left,
            width
          };
        }
        return prev;
      });
    }
  }, [activeTab, tabElements]);
  useEffect(() => {
    updateIndicatorPosition();
  }, [updateIndicatorPosition]);
  const handleTabChange = useCallback((tabValue: string) => {
    if (mode === 'navigation') {
      onTabClick?.(tabValue);
    } else {
      onChange?.(tabValue);
    }
  }, [mode, onTabClick, onChange]);
  const contextValue: TabsContextValue = useMemo(() => ({
    activeTab: activeTab || '',
    onTabChange: handleTabChange,
    variant,
    mode,
    registerTab,
    unregisterTab
  }), [activeTab, handleTabChange, variant, mode, registerTab, unregisterTab]);
  return <TabsContext.Provider value={contextValue}>
      <TabsContainer className={className} data-id={dataId}>
        <TabsList ref={tabsListRef} className={variant} role="tablist" aria-label={ariaLabel}>
          {Children.map(children, child => {
          if (isValidElement(child)) {
            return cloneElement(child, {
              ...child.props,
              variant
            });
          }
          return child;
        })}
          <ActiveIndicator style={{
          left: `${indicatorStyle.left}px`,
          width: `${indicatorStyle.width}px`
        }} />
        </TabsList>
        {withDivider && <DividerWrapper>
            <Divider />
          </DividerWrapper>}
      </TabsContainer>
    </TabsContext.Provider>;
};

require("./Tabs.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Tabs.tsx");